import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  CreditCard,
  Wallet,
  Banknote,
  ShoppingBag,
  Building2,
  Eye,
  EyeOff,
  Save,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Pencil,
  X,
  ShieldCheck,
  Calendar,
  Zap,
} from 'lucide-react'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import {
  listPaymentMethods,
  updatePaymentMethod,
  type PaymentMethod,
  type PaymentMethodPatch,
  type PaymentMode,
} from '../lib/api/payment-methods'

/* ------------------------------------------------------------------ */
/*  Helpers visuales por método                                        */
/* ------------------------------------------------------------------ */

type IconSpec = { icon: typeof CreditCard; bg: string; fg: string }

const ICON_BY_KEY: Record<string, IconSpec> = {
  mp_card: { icon: CreditCard, bg: 'bg-cyan-50', fg: 'text-cyan-700' },
  mercadopago: { icon: Wallet, bg: 'bg-sky-50', fg: 'text-sky-700' },
  paypal: { icon: ShieldCheck, bg: 'bg-blue-50', fg: 'text-blue-700' },
  oxxo: { icon: ShoppingBag, bg: 'bg-red-50', fg: 'text-red-700' },
  transferencia: { icon: Building2, bg: 'bg-emerald-50', fg: 'text-emerald-700' },
  aplazo: { icon: Zap, bg: 'bg-teal-50', fg: 'text-teal-700' },
}

function iconFor(key: string): IconSpec {
  return ICON_BY_KEY[key] ?? { icon: Banknote, bg: 'bg-ink-100', fg: 'text-ink-700' }
}

/* ------------------------------------------------------------------ */
/*  Campos editables por método                                        */
/* ------------------------------------------------------------------ */

/**
 * Cada método pinta solo los campos que realmente usa. El backend acepta
 * todos los campos, simplemente ignora los no enviados (no mete UPDATE).
 *
 * Para los métodos que guardan datos personalizados (transferencia: banco,
 * cuenta, clabe, beneficiario, rfc) usamos extra_config como un map JSON.
 */
type FieldKind = 'public' | 'secret'
type CoreField = {
  kind: FieldKind
  key: 'publicKey' | 'accessToken' | 'clientId' | 'clientSecret'
  label: string
  placeholder?: string
  mono?: boolean
}
type ExtraField = {
  kind: FieldKind
  key: string  // dentro de extraConfig
  label: string
  placeholder?: string
  mono?: boolean
}
type MethodSchema = {
  description: string
  coreFields: CoreField[]
  extraFields: ExtraField[]
}

const SCHEMA: Record<string, MethodSchema> = {
  mp_card: {
    description: 'Card Payment Brick de MercadoPago (cobro con tarjeta).',
    coreFields: [
      { kind: 'public', key: 'publicKey', label: 'Public Key', mono: true, placeholder: 'APP_USR-...' },
      { kind: 'secret', key: 'accessToken', label: 'Access Token', mono: true, placeholder: 'APP_USR-...' },
    ],
    extraFields: [],
  },
  mercadopago: {
    description: 'Checkout Pro de MercadoPago (redirección al wallet).',
    coreFields: [
      { kind: 'public', key: 'publicKey', label: 'Public Key', mono: true, placeholder: 'APP_USR-...' },
      { kind: 'secret', key: 'accessToken', label: 'Access Token', mono: true, placeholder: 'APP_USR-...' },
    ],
    extraFields: [
      { kind: 'secret', key: 'webhook_secret', label: 'Webhook secret (opcional)', mono: true },
    ],
  },
  paypal: {
    description: 'PayPal Orders API v2.',
    coreFields: [
      { kind: 'public', key: 'clientId', label: 'Client ID', mono: true },
      { kind: 'secret', key: 'clientSecret', label: 'Client Secret', mono: true },
    ],
    extraFields: [
      { kind: 'public', key: 'webhook_id', label: 'Webhook ID (opcional)', mono: true },
    ],
  },
  oxxo: {
    description: 'Cobro en efectivo OXXO Pay vía MercadoPago.',
    coreFields: [
      { kind: 'public', key: 'publicKey', label: 'Public Key', mono: true, placeholder: 'APP_USR-...' },
      { kind: 'secret', key: 'accessToken', label: 'Access Token', mono: true, placeholder: 'APP_USR-...' },
    ],
    extraFields: [],
  },
  transferencia: {
    description: 'Pago manual con depósito o transferencia bancaria.',
    coreFields: [],
    extraFields: [
      { kind: 'public', key: 'banco', label: 'Banco', placeholder: 'BBVA, Santander, …' },
      { kind: 'public', key: 'beneficiario', label: 'Beneficiario', placeholder: 'Razón social' },
      { kind: 'public', key: 'cuenta', label: 'Cuenta', mono: true },
      { kind: 'public', key: 'clabe', label: 'CLABE', mono: true },
      { kind: 'public', key: 'rfc', label: 'RFC', mono: true },
    ],
  },
}

function schemaFor(key: string): MethodSchema {
  return SCHEMA[key] ?? { description: '', coreFields: [], extraFields: [] }
}

/* ------------------------------------------------------------------ */
/*  Página                                                             */
/* ------------------------------------------------------------------ */

export function PaymentMethods() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<PaymentMethod | null>(null)
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-payment-methods'],
    queryFn: listPaymentMethods,
  })

  const toggleMut = useMutation({
    mutationFn: (vars: { key: string; isActive: boolean }) =>
      updatePaymentMethod(vars.key, { isActive: vars.isActive }),
    onSuccess: () => {
      setFlash({ tipo: 'ok', msg: 'Estado actualizado' })
      qc.invalidateQueries({ queryKey: ['admin-payment-methods'] })
      setTimeout(() => setFlash(null), 2500)
    },
    onError: (e: any) =>
      setFlash({ tipo: 'err', msg: e?.message || 'No se pudo actualizar' }),
  })

  function showFlash(tipo: 'ok' | 'err', msg: string) {
    setFlash({ tipo, msg })
    setTimeout(() => setFlash(null), 3000)
  }

  const methods = data ?? []

  return (
    <>
      <Topbar
        title="Métodos de pago"
        subtitle="Activa, cambia entorno y administra credenciales"
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? (
            <CheckCircle2 className="size-4" />
          ) : (
            <AlertCircle className="size-4" />
          )}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        <div className="pm-grid">
          {/* Eyebrow */}
          <div className="pm-eyebrow">Pasarelas activas</div>

          {isLoading && (
            <div className="flex items-center gap-2 rounded-2xl border border-ink-200 bg-white p-8 text-ink-500">
              <Loader2 className="size-4 animate-spin" />
              Cargando métodos de pago…
            </div>
          )}

          {isError && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
              No se pudo cargar la lista de métodos. Recarga la página o
              verifica que estés autenticado como admin.
            </div>
          )}

          {!isLoading && !isError && methods.length === 0 && (
            <div className="rounded-2xl border border-ink-200 bg-white p-6 text-sm text-ink-500">
              No hay métodos configurados. Ejecuta el seed de
              <code className="mx-1 rounded bg-ink-100 px-1 font-mono text-xs">
                payment_methods
              </code>
              en la base de datos.
            </div>
          )}

          <ul className="pm-list">
            {methods.map((m) => (
              <MethodCard
                key={m.methodKey}
                method={m}
                onToggle={(v) =>
                  toggleMut.mutate({ key: m.methodKey, isActive: v })
                }
                onEdit={() => setEditing(m)}
              />
            ))}
          </ul>

          <p className="pm-foot">
            Los cambios afectan inmediatamente al checkout público. Los
            secretos se guardan cifrados a nivel de BD y se muestran
            enmascarados; al guardar con el campo enmascarado intacto se
            conserva el valor anterior.
          </p>
        </div>
      </div>

      {editing && (
        <EditModal
          method={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['admin-payment-methods'] })
            showFlash('ok', 'Credenciales guardadas')
            setEditing(null)
          }}
          onError={(msg) => showFlash('err', msg)}
        />
      )}

      <style>{stylesheet}</style>
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  Card por método                                                    */
/* ------------------------------------------------------------------ */

function MethodCard({
  method,
  onToggle,
  onEdit,
}: {
  method: PaymentMethod
  onToggle: (v: boolean) => void
  onEdit: () => void
}) {
  const spec = iconFor(method.methodKey)
  const Icon = spec.icon
  const isProd = method.mode === 'production'

  return (
    <li className="pm-card">
      <div className="pm-card-left">
        <div className={`pm-ico ${spec.bg}`}>
          <Icon className={`size-5 ${spec.fg}`} />
        </div>
        <div className="pm-info">
          <div className="pm-title-row">
            <h3 className="pm-title">{method.name}</h3>
            <Badge variant={isProd ? 'success' : 'warning'}>
              {isProd ? 'production' : 'sandbox'}
            </Badge>
            <Badge variant={method.isActive ? 'info' : 'default'}>
              {method.isActive ? 'activo' : 'inactivo'}
            </Badge>
          </div>
          <p className="pm-desc">
            {method.description || schemaFor(method.methodKey).description}
          </p>
          <div className="pm-meta">
            <span>
              <code>{method.methodKey}</code>
            </span>
            {method.hasAccessToken && (
              <span>· token: {method.accessTokenMasked}</span>
            )}
            {method.hasClientSecret && (
              <span>· secret: {method.clientSecretMasked}</span>
            )}
          </div>
        </div>
      </div>

      <div className="pm-card-right">
        <Switch checked={method.isActive} onChange={onToggle} />
        <button type="button" className="pm-btn-edit" onClick={onEdit}>
          <Pencil className="size-3.5" />
          Editar credenciales
        </button>
        {method.methodKey === 'aplazo' && <AplazoSettlementButton />}
      </div>
    </li>
  )
}

function AplazoSettlementButton() {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      className="pm-btn-edit"
      onClick={() => navigate({ to: '/aplazo-settlement' })}
      title="Ver liquidaciones semanales SFTP de Aplazo"
    >
      <Calendar className="size-3.5" />
      Liquidaciones
    </button>
  )
}

/* ------------------------------------------------------------------ */
/*  Modal de edición                                                   */
/* ------------------------------------------------------------------ */

function EditModal({
  method,
  onClose,
  onSaved,
  onError,
}: {
  method: PaymentMethod
  onClose: () => void
  onSaved: () => void
  onError: (msg: string) => void
}) {
  const schema = schemaFor(method.methodKey)

  // Estado inicial del form: arrancamos con los valores actuales (los
  // secretos ya vienen enmascarados desde el backend). Si el admin no
  // los toca, los reenviamos enmascarados y el backend NO los pisa.
  const initialCore = useMemo<Record<string, string>>(() => {
    const o: Record<string, string> = {}
    for (const f of schema.coreFields) {
      const v =
        f.key === 'publicKey' ? (method.publicKey ?? '') :
        f.key === 'accessToken' ? (method.accessTokenMasked ?? '') :
        f.key === 'clientId' ? (method.clientId ?? '') :
        f.key === 'clientSecret' ? (method.clientSecretMasked ?? '') :
        ''
      o[f.key] = v
    }
    return o
  }, [method, schema])

  const initialExtra = useMemo<Record<string, string>>(() => {
    const o: Record<string, string> = {}
    const cfg = method.extraConfig ?? {}
    for (const f of schema.extraFields) {
      const v = cfg[f.key]
      o[f.key] = typeof v === 'string' ? v : ''
    }
    return o
  }, [method, schema])

  const [mode, setMode] = useState<PaymentMode>(method.mode)
  const [coreVals, setCoreVals] = useState<Record<string, string>>(initialCore)
  const [extraVals, setExtraVals] = useState<Record<string, string>>(initialExtra)
  const [visible, setVisible] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setMode(method.mode)
    setCoreVals(initialCore)
    setExtraVals(initialExtra)
    setVisible({})
  }, [method, initialCore, initialExtra])

  const saveMut = useMutation({
    mutationFn: async () => {
      const patch: PaymentMethodPatch = { mode }
      // Core
      for (const f of schema.coreFields) {
        const v = coreVals[f.key] ?? ''
        // Mandamos todo, el backend ignora enmascarados.
        if (f.key === 'publicKey') patch.publicKey = v || null
        if (f.key === 'accessToken') patch.accessToken = v || null
        if (f.key === 'clientId') patch.clientId = v || null
        if (f.key === 'clientSecret') patch.clientSecret = v || null
      }
      // Extra — solo si hay campos definidos en schema, evitamos
      // borrar accidentalmente un extra_config preexistente con otras
      // llaves no documentadas.
      if (schema.extraFields.length > 0) {
        // Partimos del extra_config actual del método para preservar
        // llaves desconocidas y solo sobrescribir las que toca el form.
        const prev = (method.extraConfig ?? {}) as Record<string, unknown>
        const merged: Record<string, unknown> = { ...prev }
        for (const f of schema.extraFields) {
          merged[f.key] = extraVals[f.key] ?? ''
        }
        patch.extraConfig = merged
      }
      return updatePaymentMethod(method.methodKey, patch)
    },
    onSuccess: () => onSaved(),
    onError: (e: any) => onError(e?.message || 'No se pudo guardar'),
  })

  function setCore(k: string, v: string) {
    setCoreVals((o) => ({ ...o, [k]: v }))
  }
  function setExtra(k: string, v: string) {
    setExtraVals((o) => ({ ...o, [k]: v }))
  }
  function toggleVisible(k: string) {
    setVisible((o) => ({ ...o, [k]: !o[k] }))
  }

  return (
    <div className="pm-modal-backdrop" onClick={onClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pm-modal-head">
          <div>
            <div className="pm-eyebrow pm-eyebrow-sm">Credenciales</div>
            <h2 className="pm-modal-title">{method.name}</h2>
            <p className="pm-modal-sub">{schema.description}</p>
          </div>
          <button type="button" className="pm-close" onClick={onClose}>
            <X className="size-4" />
          </button>
        </div>

        <div className="pm-modal-body">
          {/* Entorno */}
          <fieldset className="pm-fieldset">
            <legend className="pm-legend">Entorno</legend>
            <div className="pm-radio-row">
              <label className="pm-radio">
                <input
                  type="radio"
                  name="mode"
                  value="sandbox"
                  checked={mode === 'sandbox'}
                  onChange={() => setMode('sandbox')}
                />
                <span>Sandbox (pruebas)</span>
              </label>
              <label className="pm-radio">
                <input
                  type="radio"
                  name="mode"
                  value="production"
                  checked={mode === 'production'}
                  onChange={() => setMode('production')}
                />
                <span>Production (cobros reales)</span>
              </label>
            </div>
          </fieldset>

          {/* Core fields */}
          {schema.coreFields.length > 0 && (
            <fieldset className="pm-fieldset">
              <legend className="pm-legend">Credenciales del API</legend>
              <div className="pm-fields">
                {schema.coreFields.map((f) => (
                  <FormField
                    key={f.key}
                    label={f.label}
                    value={coreVals[f.key] ?? ''}
                    onChange={(v) => setCore(f.key, v)}
                    secret={f.kind === 'secret'}
                    visible={!!visible[`core:${f.key}`]}
                    onToggle={() => toggleVisible(`core:${f.key}`)}
                    mono={f.mono}
                    placeholder={f.placeholder}
                  />
                ))}
              </div>
            </fieldset>
          )}

          {/* Extra fields */}
          {schema.extraFields.length > 0 && (
            <fieldset className="pm-fieldset">
              <legend className="pm-legend">Datos extra</legend>
              <div className="pm-fields">
                {schema.extraFields.map((f) => (
                  <FormField
                    key={f.key}
                    label={f.label}
                    value={extraVals[f.key] ?? ''}
                    onChange={(v) => setExtra(f.key, v)}
                    secret={f.kind === 'secret'}
                    visible={!!visible[`extra:${f.key}`]}
                    onToggle={() => toggleVisible(`extra:${f.key}`)}
                    mono={f.mono}
                    placeholder={f.placeholder}
                  />
                ))}
              </div>
            </fieldset>
          )}
        </div>

        <div className="pm-modal-foot">
          <button type="button" className="pm-btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="pm-btn-primary"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending}
          >
            {saveMut.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Save className="size-3.5" />
            )}
            Guardar
          </button>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Mini componentes                                                   */
/* ------------------------------------------------------------------ */

function FormField({
  label,
  value,
  onChange,
  secret = false,
  visible,
  onToggle,
  mono,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  secret?: boolean
  visible: boolean
  onToggle: () => void
  mono?: boolean
  placeholder?: string
}) {
  return (
    <div className="pm-field">
      <label className="pm-label">{label}</label>
      <div className="pm-input-wrap">
        <input
          type={secret && !visible ? 'password' : 'text'}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`pm-input ${mono ? 'pm-input-mono' : ''}`}
        />
        {secret && (
          <button
            type="button"
            className="pm-toggle"
            onClick={onToggle}
            title={visible ? 'Ocultar' : 'Mostrar'}
          >
            {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        )}
      </div>
    </div>
  )
}

function Switch({
  checked,
  onChange,
}: {
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="pm-switch">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="pm-switch-track">
        <span className="pm-switch-thumb" />
      </span>
    </label>
  )
}

/* ------------------------------------------------------------------ */
/*  CSS modular (estilo Maz)                                           */
/* ------------------------------------------------------------------ */

const stylesheet = `
.pm-grid { display: grid; gap: 1rem; }
.pm-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 10.5px;
  font-weight: 700;
  color: #E85230;
}
.pm-eyebrow-sm { letter-spacing: 0.14em; font-size: 10px; color: #E85230; }
.pm-foot { color: #64748b; font-size: 12px; margin-top: 0.25rem; }

.pm-list { display: flex; flex-direction: column; gap: 12px; margin: 0; padding: 0; list-style: none; }
.pm-card {
  background: #ffffff;
  border: 1px solid #e6e8ee;
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  gap: 14px;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 0 rgba(15,24,37,0.02), 0 6px 16px rgba(15,24,37,0.04);
  transition: box-shadow .2s ease, transform .2s ease;
}
.pm-card:hover {
  box-shadow: 0 1px 0 rgba(15,24,37,0.03), 0 12px 26px rgba(15,24,37,0.07);
}
.pm-card-left { display: flex; gap: 14px; align-items: flex-start; flex: 1; min-width: 0; }
.pm-ico {
  width: 44px; height: 44px;
  border-radius: 12px;
  display: grid; place-items: center;
  flex-shrink: 0;
}
.pm-info { min-width: 0; }
.pm-title-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.pm-title { font-size: 15px; font-weight: 700; color: #0f1825; margin: 0; }
.pm-desc { color: #475569; font-size: 13px; margin: 4px 0 0 0; }
.pm-meta {
  margin-top: 6px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  font-size: 11px;
  color: #94a3b8;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
.pm-meta code { background: #f1f5f9; padding: 1px 6px; border-radius: 4px; color: #475569; }

.pm-card-right { display: flex; align-items: center; gap: 14px; flex-shrink: 0; }
.pm-btn-edit {
  display: inline-flex; align-items: center; gap: 6px;
  background: linear-gradient(180deg, #E85230 0%, #d24a2b 100%);
  color: #fff;
  border: none;
  font-size: 13px; font-weight: 600;
  padding: 8px 14px;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 6px 14px rgba(232,82,48,0.28);
  transition: transform .15s ease, box-shadow .15s ease;
}
.pm-btn-edit:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 20px rgba(232,82,48,0.34);
}

/* Switch */
.pm-switch { position: relative; display: inline-block; width: 44px; height: 24px; cursor: pointer; flex-shrink: 0; }
.pm-switch input { position: absolute; opacity: 0; width: 0; height: 0; }
.pm-switch-track {
  position: absolute; inset: 0;
  background: #d8dde6;
  border-radius: 999px;
  transition: background-color .15s ease;
}
.pm-switch-thumb {
  position: absolute; top: 2px; left: 2px;
  width: 20px; height: 20px;
  background: #fff;
  border-radius: 999px;
  box-shadow: 0 1px 3px rgba(15,24,37,0.2);
  transition: transform .15s ease;
}
.pm-switch input:checked + .pm-switch-track { background: #E85230; }
.pm-switch input:checked + .pm-switch-track .pm-switch-thumb { transform: translateX(20px); }

/* Modal */
.pm-modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(15,24,37,0.55);
  display: grid; place-items: center;
  z-index: 50;
  padding: 16px;
  backdrop-filter: blur(2px);
}
.pm-modal {
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: calc(100vh - 32px);
  display: flex; flex-direction: column;
  box-shadow: 0 30px 60px rgba(15,24,37,0.35);
  overflow: hidden;
}
.pm-modal-head {
  padding: 18px 20px;
  border-bottom: 1px solid #eef0f4;
  display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
}
.pm-modal-title { font-size: 17px; font-weight: 700; color: #0f1825; margin: 4px 0 2px; }
.pm-modal-sub { font-size: 12.5px; color: #64748b; margin: 0; }
.pm-close {
  border: none; background: transparent;
  color: #64748b; cursor: pointer;
  padding: 6px; border-radius: 8px;
}
.pm-close:hover { background: #f1f5f9; color: #0f1825; }

.pm-modal-body { padding: 16px 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 14px; }
.pm-fieldset {
  border: 1px solid #eef0f4;
  border-radius: 12px;
  padding: 14px 14px 12px;
  background: #fafbfc;
}
.pm-legend {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #0f1825;
  font-weight: 700;
  padding: 0 6px;
}
.pm-fields { display: grid; gap: 10px; }
.pm-field { display: flex; flex-direction: column; gap: 4px; }
.pm-label { font-size: 12px; font-weight: 600; color: #334155; }
.pm-input-wrap { position: relative; }
.pm-input {
  width: 100%;
  border: 1px solid #d8dde6;
  background: #fff;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: #0f1825;
  outline: none;
  transition: border-color .15s ease, box-shadow .15s ease;
}
.pm-input:focus { border-color: #E85230; box-shadow: 0 0 0 3px rgba(232,82,48,0.12); }
.pm-input-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; }
.pm-toggle {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  background: transparent; border: none; cursor: pointer;
  color: #64748b; padding: 4px; border-radius: 6px;
}
.pm-toggle:hover { background: #f1f5f9; color: #0f1825; }
.pm-input:has(+ .pm-toggle), .pm-input { padding-right: 32px; }

.pm-radio-row { display: flex; gap: 16px; flex-wrap: wrap; }
.pm-radio { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; color: #334155; cursor: pointer; }
.pm-radio input { accent-color: #E85230; }

.pm-modal-foot {
  padding: 14px 20px;
  border-top: 1px solid #eef0f4;
  display: flex; justify-content: flex-end; gap: 8px;
  background: #fafbfc;
}
.pm-btn-ghost {
  background: #fff;
  border: 1px solid #d8dde6;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 10px;
  cursor: pointer;
}
.pm-btn-ghost:hover { background: #f1f5f9; }
.pm-btn-primary {
  display: inline-flex; align-items: center; gap: 6px;
  background: linear-gradient(180deg, #E85230 0%, #d24a2b 100%);
  color: #fff;
  border: none;
  font-size: 13px; font-weight: 600;
  padding: 8px 16px;
  border-radius: 10px;
  cursor: pointer;
  box-shadow: 0 6px 14px rgba(232,82,48,0.28);
}
.pm-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.pm-btn-primary:not(:disabled):hover { transform: translateY(-1px); box-shadow: 0 10px 20px rgba(232,82,48,0.34); }

@media (max-width: 640px) {
  .pm-card { flex-direction: column; align-items: stretch; }
  .pm-card-right { justify-content: space-between; }
}
`
